import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  RadioGroup,
  RadioGroupItem,
} from '../components/ui/radio-group';
import {
  ShieldCheck,
  KeyRound,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertTriangle,
  Save,
  Beaker,
  Trash2,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getCashfreeIntegration,
  saveCashfreeIntegration,
  clearCashfreeIntegration,
  testCashfreeConnection,
} from '../api';

const IntegrationsSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [config, setConfig] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    client_id: '',
    client_secret: '',
    environment: 'sandbox',
  });

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await getCashfreeIntegration();
      setConfig(data);
      setForm((f) => ({ ...f, environment: data.environment || 'sandbox' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load integration settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.client_id.trim() || !form.client_secret.trim()) {
      toast.error('CLIENT_ID and CLIENT_SECRET are required');
      return;
    }
    setSaving(true);
    setTestResult(null);
    try {
      const { data } = await saveCashfreeIntegration({
        client_id: form.client_id.trim(),
        client_secret: form.client_secret.trim(),
        environment: form.environment,
      });
      setConfig(data);
      setForm({ client_id: '', client_secret: '', environment: data.environment });
      toast.success('Cashfree credentials saved. New calls will use these keys immediately.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await testCashfreeConnection();
      setTestResult({ ok: true, ...data });
      toast.success('Cashfree connection verified!');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Connection test failed';
      setTestResult({ ok: false, message: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(
      'Remove Cashfree credentials from the database?\n\n' +
      'The system will fall back to env vars if set, otherwise eKYC will return 503 until you re-save.'
    )) return;
    setClearing(true);
    try {
      await clearCashfreeIntegration();
      toast.success('Cashfree credentials cleared');
      setTestResult(null);
      await fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear credentials');
    } finally {
      setClearing(false);
    }
  };

  const isConfigured = config?.configured;
  const sourceLabel = {
    db: { text: 'Saved in database', color: 'bg-emerald-100 text-emerald-700' },
    env: { text: 'Loaded from env vars', color: 'bg-amber-100 text-amber-800' },
    none: { text: 'Not configured', color: 'bg-rose-100 text-rose-700' },
  }[config?.source || 'none'];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 p-6" data-testid="integrations-settings-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <ShieldCheck className="text-indigo-600" />
            Integrations
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Manage third-party API credentials without redeploying. Changes take effect on the very next call.
          </p>
        </div>

        {/* Cashfree Card */}
        <Card className="border-2 border-slate-200" data-testid="cashfree-integration-card">
          <CardHeader className="border-b bg-slate-50">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <KeyRound size={18} className="text-indigo-600" />
                  Cashfree Verification Suite
                </CardTitle>
                <CardDescription className="mt-1">
                  Live PAN, Aadhaar (UIDAI OTP) and Bank penny-drop verification against Government-of-India databases.
                </CardDescription>
              </div>
              <div className="flex flex-col items-end gap-2">
                {loading ? (
                  <Badge variant="outline">
                    <Loader2 size={12} className="animate-spin mr-1" /> Loading
                  </Badge>
                ) : (
                  <>
                    <Badge className={sourceLabel.color} data-testid="cashfree-source-badge">
                      {sourceLabel.text}
                    </Badge>
                    {isConfigured && (
                      <Badge
                        variant="outline"
                        className={
                          config.environment === 'production'
                            ? 'border-rose-300 text-rose-700'
                            : 'border-sky-300 text-sky-700'
                        }
                      >
                        {config.environment === 'production' ? '🔴 PRODUCTION' : '🧪 SANDBOX'}
                      </Badge>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6 space-y-6">
            {/* Current state */}
            {!loading && isConfigured && (
              <div className="rounded-md border border-slate-200 bg-white p-4 text-sm space-y-2">
                <p className="font-medium text-slate-700">Current credentials</p>
                <div className="grid grid-cols-2 gap-3 font-mono text-xs">
                  <div>
                    <p className="text-slate-500">CLIENT_ID</p>
                    <p data-testid="cashfree-client-id-masked">{config.client_id_masked}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">CLIENT_SECRET</p>
                    <p data-testid="cashfree-secret-masked">{config.secret_masked}</p>
                  </div>
                </div>
                {config.updated_at && (
                  <p className="text-xs text-slate-500 pt-1">
                    Last updated by <strong>{config.updated_by || 'system'}</strong> on{' '}
                    {new Date(config.updated_at).toLocaleString('en-IN')}
                  </p>
                )}
              </div>
            )}

            {!loading && !isConfigured && (
              <Alert variant="destructive" data-testid="cashfree-unconfigured-alert">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Not configured</AlertTitle>
                <AlertDescription>
                  eKYC verification calls will return <strong>503</strong> until credentials are added.
                  Get yours from{' '}
                  <a
                    href="https://merchant.cashfree.com/merchants/pg/developers"
                    target="_blank"
                    rel="noreferrer"
                    className="underline inline-flex items-center gap-1"
                  >
                    Cashfree Dashboard → Developers <ExternalLink size={11} />
                  </a>
                  .
                </AlertDescription>
              </Alert>
            )}

            {/* Form */}
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="client_id">
                  CLIENT_ID <span className="text-rose-500">*</span>
                </Label>
                <Input
                  id="client_id"
                  value={form.client_id}
                  onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  placeholder={isConfigured ? 'Enter new value to rotate' : 'CFxxxxxxxxxxxx...'}
                  autoComplete="off"
                  data-testid="cashfree-client-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_secret">
                  CLIENT_SECRET <span className="text-rose-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="client_secret"
                    type={showSecret ? 'text' : 'password'}
                    value={form.client_secret}
                    onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                    placeholder={isConfigured ? 'Enter new value to rotate' : 'cfsk_ma_test_... or cfsk_ma_prod_...'}
                    autoComplete="new-password"
                    data-testid="cashfree-client-secret-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    tabIndex={-1}
                    data-testid="cashfree-toggle-secret-visibility"
                    aria-label="Toggle secret visibility"
                  >
                    {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Environment</Label>
                <RadioGroup
                  value={form.environment}
                  onValueChange={(v) => setForm({ ...form, environment: v })}
                  className="flex gap-6"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="sandbox" id="env-sandbox" data-testid="env-sandbox" />
                    <Label htmlFor="env-sandbox" className="cursor-pointer font-normal">
                      <span className="font-medium text-sky-700">Sandbox</span>
                      <span className="text-xs text-slate-500 ml-2">
                        (test keys — free, mock responses)
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="production" id="env-production" data-testid="env-production" />
                    <Label htmlFor="env-production" className="cursor-pointer font-normal">
                      <span className="font-medium text-rose-700">Production</span>
                      <span className="text-xs text-slate-500 ml-2">
                        (live NSDL/UIDAI — billed per call)
                      </span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button type="submit" disabled={saving} data-testid="cashfree-save-btn">
                  {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                  Save credentials
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={testing || !isConfigured}
                  onClick={handleTest}
                  data-testid="cashfree-test-btn"
                >
                  {testing ? <Loader2 size={14} className="animate-spin mr-2" /> : <Beaker size={14} className="mr-2" />}
                  Test connection
                </Button>
                {isConfigured && config.source === 'db' && (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-rose-600 hover:text-rose-700"
                    disabled={clearing}
                    onClick={handleClear}
                    data-testid="cashfree-clear-btn"
                  >
                    {clearing ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
                    Clear
                  </Button>
                )}
              </div>
            </form>

            {/* Test result */}
            {testResult && (
              <Alert
                variant={testResult.ok ? 'default' : 'destructive'}
                className={testResult.ok ? 'border-emerald-300 bg-emerald-50' : ''}
                data-testid="cashfree-test-result"
              >
                {testResult.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                <AlertTitle>{testResult.ok ? 'Connection verified' : 'Connection failed'}</AlertTitle>
                <AlertDescription>
                  {testResult.message}
                  {testResult.ok && testResult.sample_response && (
                    <div className="text-xs font-mono mt-2 text-slate-600">
                      Env: {testResult.environment} · Sample ref:{' '}
                      {testResult.sample_response.reference_id ?? 'n/a'}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* IP Whitelist reminder */}
            <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-xs text-indigo-900">
              <strong>Reminder:</strong> Cashfree also enforces IP whitelisting per environment. If a
              call fails with &ldquo;IP not whitelisted&rdquo;, add the server&apos;s outbound IP under{' '}
              <em>Developers → Two-Factor Authentication → IP Whitelist</em> in the Cashfree dashboard for
              both Sandbox and Production separately.
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default IntegrationsSettings;
